import type { ApiResponse, PageData } from '@cng/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: { path: string; message: string }[];

  constructor(status: number, code: string, message: string, details?: { path: string; message: string }[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function readCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)cng_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the JSON envelope unwrap and return the Response (for file downloads). */
  raw?: boolean;
}

/** Refresh is attempted once per 401; this guards against a refresh storm. */
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-csrf-token': readCsrfToken() ?? '' },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

export async function request<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const { body, raw, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();
  const headers = new Headers(rest.headers);

  if (body !== undefined && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers.set('x-csrf-token', csrf);
  }

  const doFetch = () => fetch(url, {
    ...rest,
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : (body instanceof FormData ? body : JSON.stringify(body)),
  });

  let res = await doFetch();

  // A short-lived access token expiring mid-session should be invisible to the user.
  if (res.status === 401 && !url.startsWith('/api/auth/')) {
    if (await tryRefresh()) {
      const csrf = readCsrfToken();
      if (csrf) headers.set('x-csrf-token', csrf);
      res = await doFetch();
    }
  }

  if (raw) {
    if (!res.ok) throw new ApiError(res.status, 'REQUEST_FAILED', `Request failed (${res.status})`);
    return res as unknown as T;
  }

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(res.status, 'INVALID_RESPONSE', 'The server sent an unreadable response');
  }

  if (!res.ok || !payload || payload.ok === false) {
    const err = payload && payload.ok === false ? payload.error : null;
    throw new ApiError(
      res.status,
      err?.code ?? 'REQUEST_FAILED',
      err?.message ?? `Request failed (${res.status})`,
      err?.details,
    );
  }

  return payload.data;
}

export const api = {
  get: <T>(url: string, options?: RequestOptions) => request<T>(url, { ...options, method: 'GET' }),
  post: <T>(url: string, body?: unknown, options?: RequestOptions) =>
    request<T>(url, { ...options, method: 'POST', body }),
  patch: <T>(url: string, body?: unknown, options?: RequestOptions) =>
    request<T>(url, { ...options, method: 'PATCH', body }),
  put: <T>(url: string, body?: unknown, options?: RequestOptions) =>
    request<T>(url, { ...options, method: 'PUT', body }),
  delete: <T>(url: string, options?: RequestOptions) => request<T>(url, { ...options, method: 'DELETE' }),
};

export function fetchPageData(path: string): Promise<PageData> {
  return api.get<PageData>(`/api/public/page-data?path=${encodeURIComponent(path)}`);
}

export function buildQuery(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
